import { useEffect, useRef, useState } from 'react';
import { startOzonChat, sendOzonChatMessage } from '../../../../services/ozonRealization';

// ─────────────────────────────────────────────────────────────────────────────
// useOzonChat — reusable chat state/logic (start + send) for a single posting.
// Backed by the same /ozon-realization/{account}/chat/start|send endpoints used
// by "Повторные заказы". Any screen that already knows a posting_number can use
// this without going through the repeat-orders customer-history lookup.
// ─────────────────────────────────────────────────────────────────────────────
export function useOzonChat({ request, accountId }) {
  // { postingNumber, chatId, step: 'start'|'compose'|'sent', sending, err }
  const [chat, setChat] = useState(null);

  const startChat = async (postingNumber) => {
    setChat({ postingNumber, step: 'start', sending: true, err: null });
    try {
      const d = await startOzonChat({ request, accountId, postingNumber });
      setChat((c) => ({ ...c, chatId: d.chat_id, step: 'compose', sending: false }));
      return d;
    } catch (e) {
      const msg = e?.detail || e?.message || String(e);
      setChat((c) => ({ ...(c || { postingNumber }), step: 'start', sending: false, err: msg }));
      throw e;
    }
  };

  const sendMessage = async (text) => {
    const txt = (text || '').trim();
    if (!txt || !chat?.chatId) return;
    setChat((c) => ({ ...c, sending: true, err: null }));
    try {
      await sendOzonChatMessage({ request, accountId, chatId: chat.chatId, text: txt });
      setChat((c) => ({ ...c, step: 'sent', sending: false }));
    } catch (e) {
      const msg = e?.detail || e?.message || String(e);
      setChat((c) => ({ ...c, sending: false, err: msg }));
    }
  };

  const reset = () => setChat(null);

  return { chat, startChat, sendMessage, reset };
}

// ─────────────────────────────────────────────────────────────────────────────
// ChatWidget — presentational start/compose/sent panel. Reused inline inside
// the "Повторные заказы" customer card and standalone inside CustomerChatModal.
// ─────────────────────────────────────────────────────────────────────────────
export function ChatWidget({ chat, onStart, onSend, onCancel, postingNumber }) {
  const textRef = useRef(null);
  const isActive = chat?.postingNumber === postingNumber;

  useEffect(() => {
    if (isActive && chat?.step === 'compose') {
      setTimeout(() => textRef.current?.focus(), 50);
    }
  }, [isActive, chat?.step]);

  if (!isActive || chat?.step === 'start') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <button
          type="button"
          className="btn btn--sm"
          onClick={() => onStart(postingNumber)}
          disabled={isActive && chat?.sending}
          title="FBS/rFBS, ≤72ч. Требует Premium Plus/Pro"
          style={{ whiteSpace: 'nowrap' }}
        >
          {isActive && chat?.sending ? '💬 Открываем чат…' : '💬 Написать покупателю'}
        </button>
        {isActive && chat?.err && (
          <span style={{ fontSize: 12, color: '#e74c3c', maxWidth: 280, textAlign: 'right' }}>
            {chat.err}
          </span>
        )}
      </div>
    );
  }

  if (chat.step === 'compose') {
    return (
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--border-light, #eee)' }}>
        <p style={{ margin: '0 0 6px', fontSize: 12, color: 'var(--text-muted)' }}>chat_id: {chat.chatId}</p>
        <textarea
          ref={textRef}
          rows={3}
          placeholder="Введите сообщение (до 1000 символов)…"
          style={{ width: '100%', borderRadius: 6, border: '1px solid var(--border)', padding: '6px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
          <button className="btn btn--sm btn--primary" onClick={() => onSend(textRef.current?.value || '')} disabled={chat.sending}>
            {chat.sending ? 'Отправка…' : 'Отправить'}
          </button>
          <button className="btn btn--sm" onClick={onCancel}>Отмена</button>
        </div>
        {chat.err && (
          <p style={{ fontSize: 12, color: '#e74c3c', margin: '4px 0 0' }}>Ошибка: {chat.err}</p>
        )}
      </div>
    );
  }

  if (chat.step === 'sent') {
    return <p style={{ margin: '8px 0 0', fontSize: 12, color: '#27ae60' }}>✓ Сообщение отправлено.</p>;
  }

  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// CustomerChatModal — standalone modal to write to a customer given a known
// posting_number, without navigating away or loading the customer's full
// repeat-orders history. Used from FBS "Написать клиенту".
// ─────────────────────────────────────────────────────────────────────────────
export default function CustomerChatModal({ request, accountId, postingNumber, customerId, onClose }) {
  const { chat, startChat, sendMessage, reset } = useOzonChat({ request, accountId });

  useEffect(() => {
    startChat(postingNumber).catch(() => {});
    // eslint-disable-next-line
  }, [postingNumber]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: 'var(--surface, #fff)', borderRadius: 10, padding: 24,
          width: '100%', maxWidth: 480,
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>
            Написать покупателю{customerId ? ` ${customerId}` : ''}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        <p style={{ margin: '0 0 10px', fontSize: 12, color: 'var(--text-muted)' }}>
          Заказ: <span style={{ fontFamily: 'monospace' }}>{postingNumber}</span>
        </p>

        <ChatWidget
          chat={chat}
          postingNumber={postingNumber}
          onStart={startChat}
          onSend={sendMessage}
          onCancel={() => { reset(); onClose(); }}
        />
      </div>
    </div>
  );
}
